import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { apiLafargeContacts, apiSites } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { isAdminNational } from '@/constants/permits';
import ExportImportButtons from '@/components/ExportImportButtons';
import {
  Mail, Phone, User as UserIcon, Plus, Pencil, Trash2,
  Save, X, Search, Download, Building2, Briefcase,
} from 'lucide-react';

function ContactForm({ initial, sites, onSubmit, onCancel, busy }) {
  const [form, setForm] = useState({
    name: initial?.name || '',
    service: initial?.service || '',
    fonction: initial?.fonction || '',
    email: initial?.email || '',
    portable: initial?.portable || '',
    fixe: initial?.fixe || '',
    site_code: initial?.site_code || '',
    is_donneur_ordre: initial?.is_donneur_ordre ?? true,
  });
  const isEdit = !!initial?.id;
  const update = (k, v) => setForm({ ...form, [k]: v });

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      data-testid="contact-form-overlay"
    >
      <div className="bg-white border-2 border-stone-900 max-w-2xl w-full mt-10 p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-display font-black tracking-tight">
            {isEdit ? 'Modifier le contact' : 'Nouveau contact Lafarge'}
          </h2>
          <button onClick={onCancel} className="text-stone-500 hover:text-[#DA291C]" data-testid="contact-form-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="label-industrial">Nom complet *</label>
            <input
              data-testid="contact-form-name"
              type="text"
              className="input-industrial w-full"
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
              placeholder="Ex. LHOTELLIER Philippe"
            />
          </div>
          <div>
            <label className="label-industrial">Service</label>
            <input
              data-testid="contact-form-service"
              type="text"
              className="input-industrial w-full"
              value={form.service}
              onChange={(e) => update('service', e.target.value)}
              placeholder="Ex. Maintenance"
            />
          </div>
          <div>
            <label className="label-industrial">Fonction</label>
            <input
              data-testid="contact-form-fonction"
              type="text"
              className="input-industrial w-full"
              value={form.fonction}
              onChange={(e) => update('fonction', e.target.value)}
              placeholder="Ex. Chef de poste"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="label-industrial">Email *</label>
            <input
              data-testid="contact-form-email"
              type="email"
              className="input-industrial w-full"
              value={form.email}
              onChange={(e) => update('email', e.target.value)}
              placeholder="prenom.nom@lafarge.com"
            />
          </div>
          <div>
            <label className="label-industrial">Portable</label>
            <input
              data-testid="contact-form-portable"
              type="tel"
              className="input-industrial w-full"
              value={form.portable}
              onChange={(e) => update('portable', e.target.value)}
              placeholder="06 12 34 56 78"
            />
          </div>
          <div>
            <label className="label-industrial">Fixe</label>
            <input
              data-testid="contact-form-fixe"
              type="tel"
              className="input-industrial w-full"
              value={form.fixe}
              onChange={(e) => update('fixe', e.target.value)}
              placeholder="02 43 66 41 41"
            />
          </div>
          <div>
            <label className="label-industrial">Site (optionnel)</label>
            <select
              data-testid="contact-form-site"
              className="input-industrial w-full"
              value={form.site_code}
              onChange={(e) => update('site_code', e.target.value)}
            >
              <option value="">— Tous sites —</option>
              {sites.map((s) => (
                <option key={s.code} value={s.code}>{s.code} — {s.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <label className="inline-flex items-center gap-2 text-sm" data-testid="contact-form-do-label">
              <input
                type="checkbox"
                checked={form.is_donneur_ordre}
                onChange={(e) => update('is_donneur_ordre', e.target.checked)}
                data-testid="contact-form-do"
                className="w-4 h-4 accent-[#DA291C]"
              />
              <span className="font-semibold">Peut être désigné donneur d'ordre</span>
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-stone-200">
          <button
            onClick={onCancel}
            disabled={busy}
            className="btn-industrial-secondary"
            data-testid="contact-form-cancel"
          >
            Annuler
          </button>
          <button
            onClick={() => onSubmit(form)}
            disabled={busy || !form.name.trim()}
            className="btn-industrial-primary flex items-center gap-2"
            data-testid="contact-form-save"
          >
            <Save className="w-4 h-4" /> {isEdit ? 'Enregistrer' : 'Créer le contact'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Contacts() {
  const { user } = useAuth();
  const isAdmin = isAdminNational(user);
  const [contacts, setContacts] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} (new) | obj (edit)
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [siteFilter, setSiteFilter] = useState('');
  const [importing, setImporting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        apiLafargeContacts.list(),
        apiSites.list(),
      ]);
      setContacts(c.data);
      setSites(s.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Chargement impossible');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return contacts.filter((c) => {
      if (siteFilter && c.site_code !== siteFilter) return false;
      if (!q) return true;
      return [c.name, c.email, c.service, c.fonction]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q));
    });
  }, [contacts, search, siteFilter]);

  const handleSubmit = async (form) => {
    setBusy(true);
    try {
      if (editing?.id) {
        await apiLafargeContacts.update(editing.id, form);
        toast.success('Contact mis à jour');
      } else {
        await apiLafargeContacts.create(form);
        toast.success('Contact créé');
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur enregistrement');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Supprimer définitivement « ${c.name} » ?`)) return;
    try {
      await apiLafargeContacts.remove(c.id);
      toast.success('Contact supprimé');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Suppression impossible');
    }
  };

  const handleImport = async (mode) => {
    const label = mode === 'replace'
      ? 'Cette action va REMPLACER tous les contacts par ceux du fichier CoordonnéesLHC.xlsx. Continuer ?'
      : 'Importer / fusionner les contacts du fichier CoordonnéesLHC.xlsx ?';
    if (!window.confirm(label)) return;
    setImporting(true);
    try {
      const r = await apiLafargeContacts.importFromXlsx(mode);
      const { inserted, updated, total_source } = r.data;
      toast.success(`Import terminé : ${inserted} ajoutés, ${updated} mis à jour (source : ${total_source})`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import impossible');
    } finally {
      setImporting(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="max-w-3xl" data-testid="contacts-no-access">
        <div className="border-2 border-stone-900 p-6 bg-amber-50">
          <div className="text-sm font-bold uppercase tracking-wider text-[#DA291C]">Accès restreint</div>
          <p className="mt-2 text-stone-700">Cette page est réservée aux administrateurs nationaux.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl space-y-8" data-testid="contacts-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-stone-500 mb-2">
            Référentiel administrable
          </div>
          <h1 className="text-4xl font-display font-black tracking-tighter">
            Contacts Lafarge — Donneurs d'ordre
          </h1>
          <p className="text-stone-600 mt-3">
            {contacts.length} contact{contacts.length > 1 ? 's' : ''} référencé{contacts.length > 1 ? 's' : ''}.
            Ces personnes peuvent être désignées comme <strong>donneur d'ordre</strong> sur un permis et recevront
            les alertes email (Resend).
          </p>
        </div>
        <div className="inline-flex items-center gap-2 flex-wrap">
          <ExportImportButtons
            exportPath="/admin/export/lafarge-contacts.csv"
            importPath="/admin/import/lafarge-contacts"
            filenameHint={`contacts_lafarge_${new Date().toISOString().slice(0,10)}.csv`}
            visible={isAdmin}
            onImported={() => load()}
          />
          <button
            data-testid="contacts-import-merge"
            onClick={() => handleImport('merge')}
            disabled={importing}
            className="btn-industrial-secondary flex items-center gap-2"
            title="Importer/fusionner depuis CoordonnéesLHC.xlsx"
          >
            <Download className="w-4 h-4" /> {importing ? 'Import…' : 'xlsx (merge)'}
          </button>
          <button
            data-testid="contacts-import-replace"
            onClick={() => handleImport('replace')}
            disabled={importing}
            className="btn-industrial-secondary flex items-center gap-2 border-amber-600 text-amber-700"
            title="Remplacer toute la base par CoordonnéesLHC.xlsx"
          >
            <Download className="w-4 h-4" /> xlsx (réinit)
          </button>
          <button
            data-testid="contacts-add-btn"
            onClick={() => setEditing({})}
            className="btn-industrial-primary flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Nouveau contact
          </button>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-3 border border-stone-200 bg-white p-4">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <input
            data-testid="contacts-search"
            type="text"
            placeholder="Rechercher par nom, email, service…"
            className="input-industrial w-full pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          data-testid="contacts-site-filter"
          className="input-industrial"
          value={siteFilter}
          onChange={(e) => setSiteFilter(e.target.value)}
        >
          <option value="">Tous les sites</option>
          {sites.map((s) => (
            <option key={s.code} value={s.code}>{s.code} — {s.name}</option>
          ))}
        </select>
        <div className="text-xs text-stone-500 ml-auto">
          {filtered.length} / {contacts.length}
        </div>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="text-stone-500">Chargement…</div>
      ) : filtered.length === 0 ? (
        <div className="border-2 border-dashed border-stone-300 p-10 text-center" data-testid="contacts-empty">
          <UserIcon className="w-10 h-10 mx-auto text-stone-400" />
          <div className="mt-3 text-stone-600">Aucun contact trouvé.</div>
          <div className="mt-1 text-xs text-stone-500">
            Cliquez sur <strong>Importer (merge)</strong> pour charger CoordonnéesLHC.xlsx,
            ou créez manuellement un contact.
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto border border-stone-200 bg-white">
          <table className="w-full text-sm" data-testid="contacts-table">
            <thead className="bg-stone-50 border-b-2 border-stone-200">
              <tr className="text-left">
                <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-stone-600">Nom</th>
                <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-stone-600">Fonction / Service</th>
                <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-stone-600">Email</th>
                <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-stone-600">Téléphone</th>
                <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-stone-600">Site</th>
                <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-stone-600">D.O.</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr
                  key={c.id || c.email || c.name}
                  data-testid={`contact-row-${c.id}`}
                  className="border-b border-stone-100 hover:bg-stone-50"
                >
                  <td className="px-4 py-3 font-semibold">{c.name}</td>
                  <td className="px-4 py-3 text-stone-700">
                    {c.fonction && <div className="flex items-center gap-1.5"><Briefcase className="w-3 h-3 text-stone-400" />{c.fonction}</div>}
                    {c.service && <div className="text-xs text-stone-500 mt-0.5">{c.service}</div>}
                  </td>
                  <td className="px-4 py-3">
                    {c.email && (
                      <a href={`mailto:${c.email}`} className="text-[#DA291C] hover:underline inline-flex items-center gap-1.5">
                        <Mail className="w-3 h-3" /> {c.email}
                      </a>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {c.portable && <div className="flex items-center gap-1.5"><Phone className="w-3 h-3 text-stone-400" />{c.portable}</div>}
                    {c.fixe && c.fixe !== c.portable && <div className="text-stone-500 mt-0.5">{c.fixe}</div>}
                  </td>
                  <td className="px-4 py-3">
                    {c.site_code ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-stone-100 font-mono text-[10px] font-bold">
                        <Building2 className="w-3 h-3" /> {c.site_code}
                      </span>
                    ) : (
                      <span className="text-stone-400 text-xs italic">tous</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {c.is_donneur_ordre ? (
                      <span className="inline-block px-2 py-0.5 bg-emerald-100 text-emerald-800 text-[10px] font-bold uppercase tracking-wider">oui</span>
                    ) : (
                      <span className="text-stone-400 text-xs">non</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2 justify-end">
                      <button
                        data-testid={`contact-edit-${c.id}`}
                        onClick={() => setEditing(c)}
                        className="text-xs uppercase font-bold tracking-wider text-stone-700 hover:text-[#09090B] flex items-center gap-1"
                      >
                        <Pencil className="w-3 h-3" /> Modifier
                      </button>
                      <button
                        data-testid={`contact-delete-${c.id}`}
                        onClick={() => handleDelete(c)}
                        className="text-xs uppercase font-bold tracking-wider text-[#DA291C] hover:text-red-700 flex items-center gap-1"
                      >
                        <Trash2 className="w-3 h-3" /> Suppr.
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing !== null && (
        <ContactForm
          initial={editing?.id ? editing : null}
          sites={sites}
          onSubmit={handleSubmit}
          onCancel={() => setEditing(null)}
          busy={busy}
        />
      )}
    </div>
  );
}
